import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {SampleControlFields, SampleRestrictionStatus, SampleRoleParty} from 'constants/SampleStatusConstants';
import {SampleRestrictionStatusEditor} from 'inventory/sample-status/EditableSampleRestrictionStatusField';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

export interface SampleControlFieldsSectionProps extends SampleControlFields {
  onChangeSampleControls: (controls: Partial<SampleControlFields>) => void;
  isReadOnly?: boolean;
  extraFieldElement?: JSX.Element | null;
}

export default function SampleControlFieldsSection(props: SampleControlFieldsSectionProps): JSX.Element {
  return (
    <div
      className={classNames('u-small-spacing-bottom', css(sectionStyles.nestedBox))}
      {...getTestProps({component: 'SampleControlFieldsSection', element: 'root'})}
    >
      {props.extraFieldElement}
      <FormGroup>
        <FieldLabel label="Restriction status" labelStyle={FieldLabel.STYLES.LABEL} />
        <SampleRestrictionStatusEditor
          restrictionStatus={props.restrictionStatus}
          onChangeRestrictionStatus={(restrictionStatus) => {
            props.onChangeSampleControls({restrictionStatus});
          }}
          isHorizontal={true}
          isReadOnly={props.isReadOnly}
        />
      </FormGroup>
      {props.restrictionStatus === SampleRestrictionStatus.RESTRICTED && (
        <FormGroup
          dataTestAttrs={{component: 'SampleControlFieldsSection', element: 'restrictedUsersSection'}}
        >
          <FieldLabel label="Restricted sample users" labelStyle={FieldLabel.STYLES.LABEL} />
          <EntityInputWithSearch
            value={props.restrictedSampleUsers}
            onChange={(restrictedSampleUsers: Array<SampleRoleParty>) => {
              props.onChangeSampleControls({restrictedSampleUsers});
            }}
            allowBenchlingApps={true}
            allowOrganizations={false}
            allowTeams={true}
            size={TagsInputSize.SMALL}
            allowAdjustInputWidth={false}
            enableNewEntityInput={true}
            shouldAllowMultiple={!props.isReadOnly}
            disabled={props.isReadOnly}
            placeholder={props.isReadOnly ? 'None' : undefined}
          />
        </FormGroup>
      )}
      <FormGroup dataTestAttrs={{component: 'SampleControlFieldsSection', element: 'sampleOwnersSection'}}>
        <FieldLabel label="Sample owners" labelStyle={FieldLabel.STYLES.LABEL} />
        <EntityInputWithSearch
          value={props.sampleOwners}
          onChange={(sampleOwners: Array<SampleRoleParty>) => {
            props.onChangeSampleControls({sampleOwners});
          }}
          allowBenchlingApps={false}
          allowOrganizations={false}
          allowTeams={true}
          size={TagsInputSize.SMALL}
          allowAdjustInputWidth={false}
          enableNewEntityInput={true}
          shouldAllowMultiple={!props.isReadOnly}
          disabled={props.isReadOnly}
          placeholder={props.isReadOnly ? 'None' : undefined}
        />
      </FormGroup>
    </div>
  );
}

export const sectionStyles = StyleSheet.create({
  nestedBox: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    padding: 10,
  },
});
